import React, { useState, useRef, useEffect } from "react";
import Checkbox from "./Checkbox";
import { getFirmIcon } from "../../utils/firmUtils";
import { FIRM_TYPE_STYLES } from "../../utils/firmTypeStyles";

// Firma tipi bilinen satırlarda (ör. firma detayı kapsam seçicisi, #200)
// tip ikonu basılır; bilinmeyen eski kullanımlar ikonsuz kalır.
const FirmTypeIcon = ({ firm }) =>
  firm?.firmType ? (
    <span className="shrink-0 flex items-center">{getFirmIcon(firm, "w-3.5 h-3.5")}</span>
  ) : null;

// Kapsam ağacındaki derinlik → girinti (px). Arama yapılırken ağaç bozulduğu
// için girinti verilmez.
const INDENT_PX = 14;

const FirmDropdown = ({
  value,
  onChange,
  firms = [],
  loading = false,
  disabled = false,
  className = "",
  placeholder = "Tüm Firmalar",
  // Firma seçiminin zorunlu olduğu yerlerde (ör. Müşteri Aylık Performans Raporu)
  // "Tüm Firmalar" seçeneği gizlenir. Varsayılan true — mevcut kullanımlar
  // etkilenmez.
  showAllOption = true,
  required = false,
  // Çoklu seçim modu (ör. Müşteriler Yıllık Performans Raporu). Açıkken `value` bir
  // DİZİdir ve onChange yeni diziyi döndürür; menü seçimde kapanmaz.
  // Varsayılan false — mevcut tekli kullanımlar etkilenmez.
  multiple = false,
  label = "Firma",
  // Kompakt varyant — araç çubuğu içinde (ör. firma detayı cihaz filtreleri)
  // etiket satırı basılmaz ve kutu diğer filtre input'larıyla aynı yüksekliğe
  // iner. Varsayılan false; mevcut form kullanımları etkilenmez.
  compact = false,
  // Çoklu modda "Tüm Firmalar"ın altında "Pasif dahil et" anahtarı (ör.
  // Müşteri Performans). Kapalıyken "Tüm Firmalar" yalnız aktifleri seçer;
  // pasif firmalar listede kalır, tek tek seçilebilir.
  inactiveToggle = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Filter firms based on search term
  const filteredFirms = firms.filter(
    (firm) =>
      firm.firmName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      firm.cityName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      firm.townName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm("");
    }
  };

  const selectedList = multiple ? (Array.isArray(value) ? value : []) : [];
  const hasInactive = firms.some((f) => f.isActive === false);
  const showInactiveToggle = multiple && showAllOption && inactiveToggle && hasInactive;
  // "Tüm Firmalar"ın seçtiği küme
  const allTargets =
    showInactiveToggle && !includeInactive ? firms.filter((f) => f.isActive !== false) : firms;
  const allFirmsSelected =
    multiple &&
    allTargets.length > 0 &&
    allTargets.every((f) => selectedList.some((s) => s._id === f._id));
  // "Tüm Firmalar" seçili mi? Tekli modda boş değer de "tümü" demektir —
  // o zaman alttaki firmalar da seçili görünür, çoklu moddaki gibi (#223).
  const allChosen = multiple
    ? allFirmsSelected
    : showAllOption && (!value?._id || value._id === "all");
  // Çoklu modda yalnız seçim listesine bakılır — "Tüm Firmalar" pasifleri
  // dışarıda bırakabilir (inactiveToggle).
  const isSelected = (firm) =>
    multiple
      ? selectedList.some((f) => f._id === firm._id)
      : allChosen || value?._id === firm._id;

  const handleFirmSelect = (firm) => {
    if (multiple) {
      // Seçimden çıkarma da aynı tıklamayla yapılır; menü açık kalır ki
      // arka arkaya birkaç firma işaretlenebilsin.
      const next = selectedList.some((f) => f._id === firm._id)
        ? selectedList.filter((f) => f._id !== firm._id)
        : [...selectedList, firm];
      onChange(next);
      return;
    }
    onChange(firm);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleAllFirmsSelect = () => {
    if (multiple) {
      // Çoklu modda "Tüm Firmalar" bir aç/kapa: hepsi zaten seçiliyse seçim
      // temizlenir (silme gibi), değilse listedeki tüm firmalar seçilir.
      // Menü açık kalır — kullanıcı ardından tek tek çıkarabilsin.
      onChange(allFirmsSelected ? [] : [...allTargets]);
      return;
    }
    onChange({ _id: "all", firmName: "Tüm Firmalar" });
    setIsOpen(false);
    setSearchTerm("");
  };

  // "Tüm Firmalar" seçiliyken anahtar seçimi de günceller: açılınca pasifler
  // eklenir, kapanınca çıkarılır. Tek tek seçimde yalnız anahtar değişir.
  const handleIncludeInactive = (e) => {
    const next = e.target.checked;
    setIncludeInactive(next);
    if (!allFirmsSelected) return;
    if (next) {
      const ids = new Set(selectedList.map((f) => f._id));
      onChange([...selectedList, ...firms.filter((f) => f.isActive === false && !ids.has(f._id))]);
    } else {
      onChange(selectedList.filter((f) => f.isActive !== false));
    }
  };

  return (
    <div className={`${compact ? "" : "space-y-2"} ${className}`}>
      {!compact && (
        <label className="block text-sm font-semibold text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div
        className={`relative group ${isOpen ? "z-[80]" : ""}`}
        ref={dropdownRef}
      >
        <button
          type="button"
          onClick={toggleDropdown}
          disabled={disabled}
          className={`w-full bg-white border text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center justify-between border-gray-200 ${
            compact
              ? "px-3 py-2.5 rounded-lg text-sm"
              : "px-4 py-3 rounded-2xl transition-all duration-300 shadow-sm hover:shadow-md group-hover:shadow-lg"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <span
            className={`flex items-center gap-1.5 min-w-0 ${
              (multiple ? selectedList.length > 0 : value?.firmName)
                ? "text-gray-800"
                : "text-gray-400"
            }`}
          >
            <FirmTypeIcon firm={multiple ? selectedList[0] : value} />
            <span className="truncate">
            {multiple
              ? selectedList.length === 0
                ? placeholder
                : selectedList.length === 1
                  ? selectedList[0].firmName
                  : `${selectedList[0].firmName} +${selectedList.length - 1}`
              : value?.firmName || placeholder}
            </span>
          </span>
          <div className="flex items-center gap-2">
            {loading && (
              <div className="w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            )}
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>

        {isOpen && (
          // Menü HİÇBİR ŞEYİN altında kalmamalı: sticky tablo başlıkları,
          // kart gölgeleri vb. z-10..z-30 bandında. Yüksek z + kabın kendi
          // stacking context'i (aşağıdaki `relative z-30`) ikisi birden şart.
          <div className="absolute z-[80] w-full mt-1 bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
            {/* Search Input */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Firma ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* Dropdown List */}
            <div className="max-h-60 overflow-y-auto">
              {showAllOption && (
                <>
              {/* Tüm Firmalar Option */}
              <button
                type="button"
                onClick={handleAllFirmsSelect}
                className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors duration-200 flex items-center justify-between ${
                  allChosen
                    ? "bg-blue-50 text-blue-700"
                    : "text-gray-800"
                }`}
              >
                <div className="flex-1">
                  <span className="font-medium block">🏢 Tüm Firmalar</span>
                  <span className="text-xs text-gray-500 mt-0.5 block">
                    {showInactiveToggle && !includeInactive
                      ? "Tüm aktif firmaları dahil et"
                      : "Tüm firmaları dahil et"}
                  </span>
                </div>
                {allChosen && (
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </button>

              {showInactiveToggle && (
                <div className="px-4 py-2 border-t border-gray-100 flex justify-end">
                  <Checkbox
                    label="Pasif dahil et"
                    checked={includeInactive}
                    onChange={handleIncludeInactive}
                  />
                </div>
              )}

              {/* Divider */}
              <div className="border-t border-gray-200"></div>
                </>
              )}

              {/* Firms List */}
              {filteredFirms.length > 0 ? (
                filteredFirms.map((firm) => (
                  <button
                    key={firm._id}
                    type="button"
                    onClick={() => handleFirmSelect(firm)}
                    style={
                      !searchTerm && firm.depth > 0
                        ? { paddingLeft: 16 + firm.depth * INDENT_PX }
                        : undefined
                    }
                    className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors duration-200 flex items-center justify-between gap-2 ${
                      isSelected(firm) ? "bg-blue-50 text-blue-700" : "text-gray-800"
                    }`}
                  >
                    <FirmTypeIcon firm={firm} />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium block truncate">{firm.firmName}</span>
                      {firm.firmType === "dealer" && (
                        <span className={`text-xs font-SemiBold mt-0.5 block ${FIRM_TYPE_STYLES.dealer.icon}`}>
                          Bayi
                        </span>
                      )}
                      {/* Aktiflik bilgisi gelen listelerde pasif firma işaretli (#205). */}
                      {firm.isActive === false && (
                        <span className="text-xs font-SemiBold text-red-600 mt-0.5 block">
                          Pasif
                        </span>
                      )}
                      {firm.cityName && firm.townName && (
                        <span className="text-xs text-gray-500 mt-0.5 block">
                          {firm.cityName} / {firm.townName}
                        </span>
                      )}
                    </div>
                    {isSelected(firm) && (
                      <svg
                        className="w-4 h-4 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </button>
                ))
              ) : (
                <div className="px-4 py-6 text-center text-gray-500">
                  <svg
                    className="w-8 h-8 mx-auto mb-2 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <p className="text-sm">Firma bulunamadı</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/0 to-indigo-500/0 group-hover:from-blue-500/5 group-hover:to-indigo-500/5 transition-all duration-300 pointer-events-none"></div>
      </div>
    </div>
  );
};

export default FirmDropdown;
